import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import gaiusLogo from '@/assets/gaius-logo.webp';

export const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const legalUrls = language === 'fr' ? {
    privacy: '/confidentialite',
    terms: '/conditions-utilisation',
    legal: '/mentions-legales'
  } : {
    privacy: '/privacy-policy',
    terms: '/terms-of-service',
    legal: '/legal-notice'
  };

  const trainingPath = language === 'fr' ? '/formation-ia-avocat' : '/legal-ai-training-lawyers';

  const quickLinks = [
    { label: t('home'), path: '/' },
    { label: t('training'), path: trainingPath },
    { label: t('audit'), path: '/audit' },
    { label: t('blog'), path: '/blog' },
    { label: t('contact'), path: '/contact' }
  ];

  const legalLinks = [
    { label: t('legalNoticeTitle'), path: legalUrls.legal },
    { label: t('privacyPolicyTitle'), path: legalUrls.privacy },
    { label: t('termsTitle'), path: legalUrls.terms }
  ];

  return (
    <footer className="section-gradient-dark text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img alt="Gaius - Formation IA juridique avocat juriste" width={64} height={64} loading="lazy" decoding="async" className="h-16 w-auto" src={gaiusLogo} />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              {t('footerTagline')}
            </p>
            <div className="flex items-center space-x-2 text-white/60 text-sm">
              <MapPin className="w-4 h-4" />
              <span>{t('contactLocation')}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="text-base font-semibold text-white mb-4">{t('footerQuickLinks')}</h2>
            <ul className="space-y-3">
              {quickLinks.map((link) =>
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-white/60 text-sm hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h2 className="text-base font-semibold text-white mb-4">{t('footerLegal')}</h2>
            <ul className="space-y-3">
              {legalLinks.map((link) =>
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-white/60 text-sm hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h2 className="text-base font-semibold text-white mb-4">{t('footerConnect')}</h2>
            <div className="space-y-3">
              <a
                href="https://www.linkedin.com/company/gaius-tech/about/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-white/60 text-sm hover:text-white transition-colors">
                <Linkedin className="w-4 h-4" />
                <span>LinkedIn</span>
              </a>
              <a
                href="mailto:contact@gaius-tech.com"
                className="flex items-center space-x-2 text-white/60 text-sm hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                <span>contact@gaius-tech.com</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-3">
              <a
                href="https://www.linkedin.com/company/gaius-tech/about/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white transition-colors"
                aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <p className="text-white/60 text-sm">
                © {currentYear} Gaius. {t('allRightsReserved')}.
              </p>
            </div>
            <p className="text-white/40 text-xs">
              {language === 'fr' ?
              'Formation et conseil en IA pour les professionnels du droit. Organisme de formation certifié Qualiopi. Éligible aux financements OPCO.' :
              'AI training and consulting for legal professionals. Qualiopi-certified training organization. Eligible for OPCO funding.'
              }
            </p>
          </div>
        </div>
      </div>
    </footer>);
};
